import client from '../client';
import endpoints from '../endpoints';

/**
 * 가격 — 서비스 + 관리자 API
 * API_DOCS: 6. 가격 조회 / 20. 관리자 - 가격
 */

/* ── 서비스 (유저용) ── */

/** POST /api/price/search */
export const searchPrice = async (data) => {
  const res = await client.post(endpoints.price.search, data);
  return res.data;
};

/** GET /api/price/options */
export const getOptions = async () => {
  const res = await client.get(endpoints.price.options);
  return res.data;
};

/** GET /api/price/surcharges */
export const getSurcharges = async () => {
  const res = await client.get(endpoints.price.surcharges);
  return res.data;
};

/** GET /api/price/config/:serviceType → 전체 ServiceConfigResponse (base_hours, overtime_price, free_wait_minutes 등) */
export const getServiceConfig = async (serviceType) => {
  const res = await client.get(endpoints.price.config(serviceType));
  return res.data || {};
};

/** GET /api/price/config/:serviceType → 선택 옵션 배열 (투어는 tourId 로 상품별 스코핑) */
export const getServiceOptions = async (serviceType, tourId) => {
  const config = tourId != null ? { params: { tour_id: tourId } } : undefined;
  const res = await client.get(endpoints.price.config(serviceType), config);
  const options = res.data?.options || [];
  return options.map((o, i) => {
    // max: BE가 max 또는 max_qty 어느 쪽으로 보내든 정규화 (0/null은 무제한 → undefined)
    const rawMax = o.max ?? o.max_qty;
    const normalizedMax = typeof rawMax === 'number' && rawMax > 0 ? rawMax : undefined;
    return {
      ...o,
      id: o.id ?? o.option_id ?? String(i),
      price: Number(o.price) || 0,
      defaultQty: o.defaultQty ?? o.default_qty,
      baseQty: o.baseQty ?? o.base_qty,
      extraLabel: o.extraLabel ?? o.extra_label,
      max: normalizedMax,
    };
  });
};

/** GET /api/price/config/:serviceType → 할증 정보 */
export const getServiceSurcharges = async (serviceType) => {
  const res = await client.get(endpoints.price.config(serviceType));
  const d = res.data || {};
  // AUDIT G05 — 주말 할증 금액은 BE 가 ServiceConfigResponse.weekend_surcharge 로 반환
  //   (WEEKEND_SURCHARGE_SERVICES 에 포함된 서비스만 양수, 나머지는 null)
  const weekendAmount = Number(d.weekend_surcharge) || 0;
  const hasWeekendSurcharge = weekendAmount > 0 || !!d.weekend_surcharge_text;

  // #111: BE에서 "시간 초과 :" 로 오는 경우 "시간 초과 요금" 으로 정규화
  const normalizedNotice = (d.surcharge_notice || '').replace(/시간\s*초과\s*(?!요금)/g, '시간 초과 요금 ');
  return {
    overtime: normalizedNotice,
    overtime_price: Number(d.overtime_price) || 0,
    // AUDIT G06 — BE stopover_price 그대로 사용 (FE 폴백 29,900 제거)
    stopover_price: Number(d.stopover_price) || 0,
    weekend: hasWeekendSurcharge ? `주말/공휴일 할증: ${weekendAmount.toLocaleString('ko-KR')}원` : '',
    weekend_surcharge_amount: hasWeekendSurcharge ? weekendAmount : 0,
    surchargeTitle: d.surcharge_title || '현장 추가 요금 안내',
  };
};

/** @deprecated 백엔드 미구현 */
export const getRegionalPricing = async () => [];

/** @deprecated 백엔드 미구현 */
export const getBookingFormConfig = async () => ({});

/** @deprecated 백엔드 미구현 */
export const getWeekdaySurcharges = async () => [];

/** GET /api/vehicles + /api/buffer-settings + /api/price/config/:serviceType + /api/images */
export const getVehicleInfo = async (serviceType) => {
  const [vehicles, bufferSettings, priceConfig] = await Promise.all([
    client.get(endpoints.common.vehicles, { params: { service_type: serviceType } }).then((r) => r.data),
    client.get(endpoints.common.bufferSettings, { params: { service_type: serviceType } }).then((r) => r.data),
    client.get(endpoints.price.config(serviceType)).then((r) => r.data),
  ]);
  const defaultVehicle = vehicles.find((v) => v.is_default) || vehicles[0] || {};

  // 차량 이미지 조회
  let vehicleImages = [];
  if (defaultVehicle.id) {
    try {
      const imgs = await client.get('/api/images', {
        params: { entity_type: 'vehicle', entity_id: defaultVehicle.id },
      });
      vehicleImages = (imgs.data || []).map((img) => img.image_url);
    } catch {}
  }

  // #207: "탑승 인원: 1인(기본) ~ 6인(최대)" → "탑승 인원: 최대 N명" 으로 단순화.
  const maxP = defaultVehicle.max_passengers;
  const capacity = maxP ? `탑승 인원: 최대 ${maxP}명` : '';

  // #208: "수화물" → "수하물" 오타 수정.
  // 서비스별 수하물 보정 (BE 차량 데이터는 공통이므로 FE에서 서비스별 분기)
  const luggageOverride = {
    golf: '적정 수하물: 골프백+보스턴백 4세트(기본)',
    pet: '적정 수하물: 케이지',
  };
  const luggage = luggageOverride[serviceType]
    || (defaultVehicle.luggage ? `적정 수하물: ${defaultVehicle.luggage}` : '');

  // AUDIT G01: 공항 무료대기 행은 BE buffer_settings 에서 동적 생성
  //   pickup/sending 별 free_waiting_minutes 사용 — 운영팀이 어드민에서 분 단위 변경 가능
  const buildAirportFreeRows = () => {
    if (serviceType !== 'airport') return null;
    // #225: 모바일 가독성 — 라우트 화살표 제거, 사내 표준 표기("공항 픽업"/"공항 샌딩")로 축약.
    //   라벨은 VehicleInfoSection 에서 direction 별 정적 i18n 키로 번역(DeepL 폴백).
    //   ("샌딩"은 DeepL 이 "sanding" 으로 오역 → free_waiting_minutes 처럼 정적 키 사용)
    const dirLabel = {
      pickup:  '공항 픽업',
      sending: '공항 샌딩',
    };
    const dirOrder = { pickup: 0, sending: 1 };
    return (bufferSettings || [])
      .filter((b) => dirLabel[b.direction] && b.free_waiting_minutes != null)
      .sort((a, b) => (dirOrder[a.direction] ?? 9) - (dirOrder[b.direction] ?? 9))
      .map((b) => ({
        direction: b.direction,
        label: dirLabel[b.direction],
        value: `${b.free_waiting_minutes}분 무료 대기`,
      }));
  };

  const freeRows = buildAirportFreeRows();

  // AUDIT G02-G03 — usageTimeBadge 는 BE priceConfig.base_hours 에서 동적 생성
  //   (FE 하드코딩 'serviceConfig' 제거. 운영팀이 SERVICE_META/admin 에서 변경 시 즉시 반영)
  const usageTimeBadge = priceConfig.base_hours
    ? `기본 이용시간 ${priceConfig.base_hours}시간`
    : null;

  // AUDIT G04 — 시간 초과 요금: BE 데이터 우선 (폴백 제거)
  //   #111: BE 문구가 "시간 초과 :" 로 짧게 오는 경우 "시간 초과 요금" 으로 정규화
  //   #211: 공항만 "초과 대기 요금" 으로 표기 (클라 요청). 그 외 서비스는 기존 "시간 초과 요금" 유지.
  const _overtimeLabel = serviceType === 'airport' ? '초과 대기 요금' : '시간 초과 요금';
  const rawOvertime = priceConfig.surcharge_notice
    || (priceConfig.overtime_price != null
      ? `${_overtimeLabel}: ${Number(priceConfig.overtime_price).toLocaleString('ko-KR')}원 / 1시간 당`
      : '');
  // BE 가 짧은 "시간 초과 :" 류로 보낼 때 정규화. 공항은 "초과 대기 요금" 으로 치환.
  const overtime = (rawOvertime || '')
    .replace(/시간\s*초과\s*(?!요금)/g, '시간 초과 요금 ')
    .replace(serviceType === 'airport' ? /시간 초과 요금/g : /__none__/g, '초과 대기 요금');

  // overtimePrice(raw 숫자) + overtimeI18nKey 동반 전달 → VehicleInfoSection 이 useMoney 로
  // 통화 변환 + i18n 템플릿 적용. legacy overtime 문자열은 KRW 박힘 회피 위해 폴백 전용.
  const overtimePrice = priceConfig.overtime_price != null ? Number(priceConfig.overtime_price) : null;
  const overtimeI18nKey = serviceType === 'airport'
    ? 'booking.overtime_waiting_fee_per_hour'
    : 'booking.overtime_fee_per_hour';

  return {
    carName: defaultVehicle.name || '',
    seat: defaultVehicle.description || '',
    capacity,
    luggage,
    usageTimeBadge,
    images: vehicleImages,
    waitInfo: { overtime, overtimePrice, overtimeI18nKey, freeRows },
  };
};

/** GET /api/main/banners */
export const getLandingGallery = async () => {
  const res = await client.get(endpoints.main.banners);
  // #241: 오버레이 텍스트/스타일 필드도 함께 전달(BE 가 lang 에 맞춰 번역해 내려줌). 비어있으면 이미지만 노출.
  return res.data.map((b) => ({
    id: b.id,
    image_url: b.image_url,
    alt: b.title || '',
    link_url: b.link_url || null,
    tag_label: b.tag_label || '',
    headline: b.headline || '',
    description: b.description || '',
    text_align: b.text_align || 'left',
    overlay_dim: b.overlay_dim || 0,
  }));
};

/** @deprecated 백엔드 미구현 */
export const getLandingVehicleSlides = async () => [];

/** @deprecated 백엔드 미구현 */
export const getLandingServiceFeatures = async () => [];

/** @deprecated 백엔드 미구현 */
export const getRefundPolicy = async () => null;

/** POST /api/coupons/validate — 할인 코드 검증 */
export const validateCoupon = async (code) => {
  const res = await client.post(endpoints.coupons.validate, { code });
  return res.data;
};

/* ── 관리자 - 쿠폰 ── */

/** GET /api/admin/coupons — 쿠폰 목록 */
export const getAdminCoupons = async (params) => {
  const res = await client.get(endpoints.admin.coupons.list, { params });
  return res.data;
};

/** POST /api/admin/coupons — 쿠폰 생성 */
export const createAdminCoupon = async (data) => {
  const res = await client.post(endpoints.admin.coupons.list, data);
  return res.data;
};

/** GET /api/admin/coupons/:id — 쿠폰 상세 */
export const getAdminCoupon = async (id) => {
  const res = await client.get(endpoints.admin.coupons.detail(id));
  return res.data;
};

/** PUT /api/admin/coupons/:id — 쿠폰 수정 */
export const updateAdminCoupon = async (id, data) => {
  const res = await client.put(endpoints.admin.coupons.detail(id), data);
  return res.data;
};

/** DELETE /api/admin/coupons/:id — 쿠폰 삭제 */
export const deleteAdminCoupon = async (id) => {
  const res = await client.delete(endpoints.admin.coupons.detail(id));
  return res.data;
};

/** GET /api/admin/coupons/:id/usages — 쿠폰 사용 이력 */
export const getAdminCouponUsages = async (id) => {
  const res = await client.get(endpoints.admin.coupons.usages(id));
  return res.data;
};

/** @deprecated 백엔드 미구현 */
export const getPaymentTerms = async () => null;

/* ── 관리자 ── */

/** GET /api/admin/price/airport */
export const getAdminAirportPricing = async () => {
  const res = await client.get(endpoints.admin.price.airport);
  return res.data;
};

/** PUT /api/admin/price/airport/:regionId */
export const updateAdminAirportPricing = async (regionId, data) => {
  const res = await client.put(endpoints.admin.price.airportRegion(regionId), data);
  return res.data;
};

/** GET /api/admin/price/golf */
export const getAdminGolfPricing = async () => {
  const res = await client.get(endpoints.admin.price.golf);
  return res.data;
};

/** POST /api/admin/price/golf */
export const createAdminGolfPricing = async (data) => {
  const res = await client.post(endpoints.admin.price.golf, data);
  return res.data;
};

/** PUT /api/admin/price/golf/:pricingId */
export const updateAdminGolfPricing = async (pricingId, data) => {
  const res = await client.put(endpoints.admin.price.golfDetail(pricingId), data);
  return res.data;
};

/** DELETE /api/admin/price/golf/:pricingId */
export const deleteAdminGolfPricing = async (pricingId) => {
  const res = await client.delete(endpoints.admin.price.golfDetail(pricingId));
  return res.data;
};

/** GET /api/admin/price/golf-regional */
export const getAdminGolfRegionalPricing = async () => {
  const res = await client.get(endpoints.admin.price.golfRegional);
  return res.data;
};

/** PUT /api/admin/price/golf-regional/:pricingId */
export const updateAdminGolfRegionalPricing = async (pricingId, data) => {
  const res = await client.put(endpoints.admin.price.golfRegionalDetail(pricingId), data);
  return res.data;
};

/** GET /api/admin/price/options?service_type=xxx */
export const getAdminOptions = async (params) => {
  const res = await client.get(endpoints.admin.price.options, { params });
  return res.data;
};

/** POST /api/admin/price/options */
export const createAdminOption = async (data) => {
  const res = await client.post(endpoints.admin.price.options, data);
  return res.data;
};

/** DELETE /api/admin/price/options/:optionId */
export const deleteAdminOption = async (optionId) => {
  const res = await client.delete(endpoints.admin.price.optionDetail(optionId));
  return res.data;
};

/** GET /api/admin/price/surcharges — 조건별 할증료 */
export const adminGetConditionalSurcharges = async () => {
  const res = await client.get(endpoints.admin.price.surcharges);
  return res.data;
};

/** PUT /api/admin/price/surcharges — 조건별 할증료 수정 */
export const adminUpdateConditionalSurcharges = async (data) => {
  const res = await client.put(endpoints.admin.price.surcharges, data);
  return res.data;
};

/** GET /api/admin/price/onsite-surcharges — 현장 추가요금 */
export const adminGetOnsiteSurcharges = async () => {
  const res = await client.get(endpoints.admin.price.onsiteSurcharges);
  return res.data;
};

/** PUT /api/admin/price/onsite-surcharges — 현장 추가요금 수정 */
export const adminUpdateOnsiteSurcharges = async (data) => {
  const res = await client.put(endpoints.admin.price.onsiteSurcharges, data);
  return res.data;
};

/** GET /api/admin/price/hospital-regional — 병원 지역별 가격 */
export const adminGetHospitalRegionalPricing = async () => {
  const res = await client.get(endpoints.admin.price.hospitalRegional);
  return res.data;
};

/** PUT /api/admin/price/hospital-regional/:regionId — 병원 지역별 가격 수정 */
export const adminUpdateHospitalRegionalPricing = async (regionId, data) => {
  const res = await client.put(endpoints.admin.price.hospitalRegionalDetail(regionId), data);
  return res.data;
};

/** PUT /api/admin/price/options/:optionId */
export const updateAdminOption = async (optionId, data) => {
  const res = await client.put(endpoints.admin.price.optionDetail(optionId), data);
  return res.data;
};

/** GET /api/admin/price/pet */
export const getAdminPetPricing = async () => {
  const res = await client.get(endpoints.admin.price.pet);
  return res.data;
};

/** PUT /api/admin/price/pet */
export const updateAdminPetPricing = async (data) => {
  const res = await client.put(endpoints.admin.price.pet, data);
  return res.data;
};

/** GET /api/admin/price/business */
export const getAdminBusinessPricing = async () => {
  const res = await client.get(endpoints.admin.price.business);
  return res.data;
};

/** PUT /api/admin/price/business */
export const updateAdminBusinessPricing = async (data) => {
  const res = await client.put(endpoints.admin.price.business, data);
  return res.data;
};

/** GET /api/admin/price/ski */
export const getAdminSkiPricing = async () => {
  const res = await client.get(endpoints.admin.price.ski);
  return res.data;
};

/** PUT /api/admin/price/ski/:skiId */
export const updateAdminSkiPricing = async (skiId, data) => {
  const res = await client.put(endpoints.admin.price.skiDetail(skiId), data);
  return res.data;
};
